import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Residence, ResidenceInsert, ResidenceUpdate } from '../types'

const PROPERTIES_QUERY_KEY = ['properties']

/**
 * Fetch all properties
 * IMPORTANT: This query depends on auth state being ready
 */
export function useProperties() {
  return useQuery({
    queryKey: PROPERTIES_QUERY_KEY,
    queryFn: async () => {
      // Ensure auth session is loaded before fetching properties
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        // If no session, return empty array (user not authenticated)
        return []
      }

      const { data, error } = await supabase
        .from('residences')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Residence[]
    },
    // Wait a bit for auth state to stabilize on mount
    staleTime: 0, // Always refetch on mount
  })
}

/**
 * Fetch a single property by ID
 */
export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: [...PROPERTIES_QUERY_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error('Property ID is required')

      const { data, error } = await supabase
        .from('residences')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as Residence
    },
    enabled: !!id,
  })
}

/**
 * Create a new property
 * Automatically adds the current user as ADMIN of the property
 * Also updates user's agency_id if it's their first residence (onboarding)
 */
export function useCreateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (property: ResidenceInsert & { assignGlobalAccountants?: boolean }) => {
      const { assignGlobalAccountants = false, ...residenceData } = property
      // DEBUG: Log session info
      console.log('🔍 [DEBUG] Starting residence creation...')

      // Get current session first (includes JWT)
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession()

      console.log('🔍 [DEBUG] Session:', {
        hasSession: !!session,
        userId: session?.user?.id,
        hasAccessToken: !!session?.access_token,
        expiresAt: session?.expires_at,
        error: sessionError,
      })

      if (!session || !session.user) {
        throw new Error('No valid session - user not authenticated')
      }

      // Get current user (should be the same as session.user)
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        throw new Error('User not authenticated')
      }

      console.log('🔍 [DEBUG] User ID:', user.id)

      // Check if user has an agency_id
      const { data: userData } = await supabase
        .from('users')
        .select('agency_id')
        .eq('id', user.id)
        .single()

      console.log('🔍 [DEBUG] User agency_id:', userData?.agency_id)
      console.log('🔍 [DEBUG] Property agency_id:', property.agency_id)

      // Generate the UUID ourselves so we don't need .select() after insert
      const residenceId = crypto.randomUUID()
      console.log('🔍 [DEBUG] Generated residence ID:', residenceId)

      // Create the residence with the pre-generated ID
      // NOTE: We DON'T use .select() here because the SELECT policy requires
      // a user_residence entry, which we haven't created yet.
      console.log('🔍 [DEBUG] Attempting to insert residence...')
      const { error } = await supabase
        .from('residences')
        .insert({
          ...residenceData,
          id: residenceId, // Use our pre-generated ID
        })

      if (error) {
        console.error('🔍 [DEBUG] Insert error:', error)
        throw error
      }

      console.log('🔍 [DEBUG] Residence created successfully:', residenceId)

      // If this is the user's first residence (no agency_id yet), update their agency_id
      if (!userData?.agency_id && residenceData.agency_id) {
        const { error: updateError } = await supabase
          .from('users')
          .update({ agency_id: residenceData.agency_id })
          .eq('id', user.id)

        if (updateError) {
          console.error('Error updating user agency_id:', updateError)
          // Don't throw - the residence was created successfully
        }
      }

      // Determine the creator's role for this residence
      // 1. Check if user has existing residences
      const { data: existingUserResidences } = await supabase
        .from('user_residences')
        .select('role')
        .eq('user_id', user.id)

      let creatorRole: 'ADMIN' | 'MANAGER' = 'ADMIN' // Default to ADMIN

      if (existingUserResidences && existingUserResidences.length > 0) {
        // User has existing residences
        // If user is ADMIN on at least one residence → create as ADMIN
        // Otherwise (only MANAGER) → create as MANAGER
        const isAdminSomewhere = existingUserResidences.some((ur) => ur.role === 'ADMIN')
        creatorRole = isAdminSomewhere ? 'ADMIN' : 'MANAGER'
      }
      // else: first residence → ADMIN (default)

      console.log('🔍 [DEBUG] Creator role for new residence:', creatorRole)

      // Automatically assign the user with the determined role
      const { error: userResidenceError } = await supabase
        .from('user_residences')
        .insert({
          user_id: user.id,
          residence_id: residenceId,
          role: creatorRole,
        })

      if (userResidenceError) {
        console.error('Error creating user_residence:', userResidenceError)
        // Don't throw - the residence was created successfully
        // The user might need to be assigned manually
      }

      // CLAUDE.md Rule: If creator is MANAGER, automatically add all ADMIN of the agency
      if (creatorRole === 'MANAGER' && residenceData.agency_id) {
        console.log('🔍 [DEBUG] Creator is MANAGER, auto-adding ADMIN from agency...')

        // Find all ADMIN users of this agency
        // Strategy: Find all users who are ADMIN on at least one residence of this agency
        const { data: agencyResidences } = await supabase
          .from('residences')
          .select('id')
          .eq('agency_id', residenceData.agency_id)

        if (agencyResidences && agencyResidences.length > 0) {
          const residenceIds = agencyResidences.map((r) => r.id)

          // Find all ADMIN users for these residences
          const { data: adminUsers } = await supabase
            .from('user_residences')
            .select('user_id')
            .in('residence_id', residenceIds)
            .eq('role', 'ADMIN')

          if (adminUsers && adminUsers.length > 0) {
            // Get unique ADMIN user IDs (excluding the creator)
            const uniqueAdminIds = Array.from(
              new Set(adminUsers.map((ur) => ur.user_id))
            ).filter((id) => id !== user.id)

            console.log('🔍 [DEBUG] Found ADMIN users to add:', uniqueAdminIds.length)

            if (uniqueAdminIds.length > 0) {
              // Add them all as ADMIN to the new residence
              const { error: adminAssignError } = await supabase
                .from('user_residences')
                .insert(
                  uniqueAdminIds.map((adminId) => ({
                    user_id: adminId,
                    residence_id: residenceId,
                    role: 'ADMIN' as const,
                  }))
                )

              if (adminAssignError) {
                console.error('Error auto-assigning ADMIN users:', adminAssignError)
                // Don't throw - not critical, can be assigned manually
              } else {
                console.log('✅ [DEBUG] Successfully auto-assigned', uniqueAdminIds.length, 'ADMIN users')
              }
            }
          }
        }
      }

      // Auto-assign global accountants if requested
      if (assignGlobalAccountants && residenceData.agency_id) {
        console.log('🔍 [DEBUG] Auto-assigning global accountants...')

        // Count total residences in agency (before this new one was added to results)
        const { count: totalResidences } = await supabase
          .from('residences')
          .select('*', { count: 'exact', head: true })
          .eq('agency_id', residenceData.agency_id)

        if (totalResidences && totalResidences > 0) {
          // Find all ACCOUNTANT users with their residence count
          const { data: accountants } = await supabase
            .from('users')
            .select(`
              id,
              user_residences!inner (
                residence_id,
                role,
                residences!inner (
                  agency_id
                )
              )
            `)
            .eq('agency_id', residenceData.agency_id)
            .eq('user_residences.role', 'ACCOUNTANT')
            .eq('user_residences.residences.agency_id', residenceData.agency_id)

          if (accountants && accountants.length > 0) {
            const globalAccountantIds: string[] = []

            // Filter to keep only accountants associated with ALL residences (excluding the new one)
            for (const accountant of accountants) {
              const residenceIds = new Set(
                accountant.user_residences.map((ur: any) => ur.residence_id)
              )

              // If accountant has same number of residences as total in agency = global accountant
              if (residenceIds.size === totalResidences) {
                globalAccountantIds.push(accountant.id)
              }
            }

            console.log('🔍 [DEBUG] Found global accountants:', globalAccountantIds.length)

            if (globalAccountantIds.length > 0) {
              const { error: accountantAssignError } = await supabase
                .from('user_residences')
                .insert(
                  globalAccountantIds.map((accountantId) => ({
                    user_id: accountantId,
                    residence_id: residenceId,
                    role: 'ACCOUNTANT' as const,
                  }))
                )

              if (accountantAssignError) {
                console.error('Error auto-assigning global accountants:', accountantAssignError)
                // Don't throw - not critical
              } else {
                console.log('✅ [DEBUG] Successfully auto-assigned', globalAccountantIds.length, 'global accountants')
              }
            }
          }
        }
      }

      // NOW we can fetch the full residence data since user_residence exists
      const { data: fullResidence, error: fetchError } = await supabase
        .from('residences')
        .select('*')
        .eq('id', residenceId)
        .single()

      if (fetchError) {
        console.error('Error fetching created residence:', fetchError)
        // Return minimal data if fetch fails
        return { id: residenceId, ...property } as Residence
      }

      return fullResidence as Residence
    },
    onSuccess: () => {
      // Invalidate both properties and auth queries
      // Auth needs to be refetched to include the new residence
      queryClient.invalidateQueries({ queryKey: PROPERTIES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['auth'] })
      queryClient.invalidateQueries({ queryKey: ['agencies'] })
    },
  })
}

/**
 * Update an existing property
 */
export function useUpdateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: ResidenceUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('residences')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Residence
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: PROPERTIES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...PROPERTIES_QUERY_KEY, data.id] })
    },
  })
}

/**
 * Delete a property
 */
export function useDeleteProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('residences').delete().eq('id', id)

      if (error) throw error
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROPERTIES_QUERY_KEY })
    },
  })
}

/**
 * Get property statistics (units count, active owners count, and total shares)
 */
export function usePropertyStats(residenceId: string | undefined) {
  return useQuery({
    queryKey: [...PROPERTIES_QUERY_KEY, 'stats', residenceId],
    queryFn: async () => {
      if (!residenceId) throw new Error('Residence ID is required')

      // Ensure auth session is loaded before fetching stats
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        // If no session, return empty stats
        return {
          unitsCount: 0,
          ownersCount: 0,
          totalShares: 0,
        }
      }

      // Get units count and sum of general_shares (tantièmes)
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('id, general_shares')
        .eq('residence_id', residenceId)
        .is('deleted_at', null)

      if (unitsError) {
        console.error('Error fetching units for stats:', unitsError)
        throw unitsError
      }

      const unitsCount = unitsData?.length || 0
      const totalShares = unitsData?.reduce((sum, unit) => sum + (unit.general_shares || 0), 0) || 0

      // Get active owners count - avoid complex join, do it in two steps
      // First get all unit IDs for this residence
      const unitIds = unitsData?.map((u) => u.id) || []

      let ownersCount = 0
      if (unitIds.length > 0) {
        // Then get ownerships for these units
        const { data: ownershipsData, error: ownershipsError } = await supabase
          .from('ownerships')
          .select('owner_id')
          .in('unit_id', unitIds)
          .is('end_date', null)

        if (ownershipsError) {
          console.error('Error fetching ownerships for stats:', ownershipsError)
          // Don't throw, just log - we can still show units count
        } else {
          // Count distinct owners
          const uniqueOwners = new Set(ownershipsData?.map((o) => o.owner_id) || [])
          ownersCount = uniqueOwners.size
        }
      }

      return {
        unitsCount,
        ownersCount,
        totalShares,
      }
    },
    enabled: !!residenceId,
    // Always refetch on mount to ensure fresh data with current session
    staleTime: 0,
  })
}

/**
 * Get property manager
 */
export function usePropertyManager(residenceId: string | undefined) {
  return useQuery({
    queryKey: [...PROPERTIES_QUERY_KEY, 'manager', residenceId],
    queryFn: async () => {
      if (!residenceId) throw new Error('Residence ID is required')

      // Ensure auth session is loaded
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        return null
      }

      // Use RPC function to get manager (bypasses RLS)
      const { data, error } = await supabase
        .rpc('get_residence_manager' as any, {
          p_residence_id: residenceId
        }) as any

      if (error) {
        console.error('Error fetching property manager:', error)
        throw error
      }

      // The function returns a table, so data is an array
      if (!data || data.length === 0) return null

      const manager = data[0]
      return `${manager.first_name} ${manager.last_name}`
    },
    enabled: !!residenceId,
    // Always refetch on mount to ensure fresh data with current session
    staleTime: 0,
  })
}
